import { useState } from 'react';
import { Loader2, Calendar, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Select } from '@travellegacy/nexops-design-system';
import { useCheckIns } from '../hooks/useCheckIns';
import { MoodBadge } from '../components/MoodSelector';
import { CreateCheckInForm } from '../components/CreateCheckInForm';
import { useAuth } from '@/shared/contexts/AuthContext';
import { Permission, hasPermission } from '@/shared/types/permissions';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Página de Check-ins (listagem)
 */
export function CheckInsPage() {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [showForm, setShowForm] = useState(false);

  const { user } = useAuth();
  const { data, isLoading } = useCheckIns({
    month: selectedMonth,
    year: selectedYear,
  });

  // Apenas gestores podem criar check-ins
  const canCreateCheckIn = hasPermission(
    user?.role || '',
    user?.permissions,
    Permission.FEEDBACK_PDI_MANAGEMENT
  );

  // Se está mostrando o formulário, renderiza apenas ele
  if (showForm) {
    return (
      <CreateCheckInForm
        onCancel={() => setShowForm(false)}
        onSuccess={() => setShowForm(false)}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Check-ins Mensais</h1>
          <p className="text-muted-foreground">Conversas 1:1 com os colaboradores</p>
        </div>

        {canCreateCheckIn && (
          <Button onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Registrar Check-in
          </Button>
        )}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-4">
        <Select
          label="Mês"
          value={selectedMonth.toString()}
          onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
          options={Array.from({ length: 12 }, (_, i) => i + 1).map((month) => ({
            value: month.toString(),
            label: new Date(2024, month - 1).toLocaleString('pt-BR', { month: 'long' }),
          }))}
        />

        <Select
          label="Ano"
          value={selectedYear.toString()}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          options={[2024, 2025, 2026].map((year) => ({
            value: year.toString(),
            label: year.toString(),
          }))}
        />
      </div>

      {/* Lista de Check-ins */}
      <div className="space-y-4">
        {data && data.checkIns.length > 0 ? (
          data.checkIns.map((checkIn) => (
            <Card key={checkIn.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">{checkIn.employee.user.name}</CardTitle>
                    <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                      <Calendar className="h-4 w-4" />
                      {new Date(checkIn.date).toLocaleDateString('pt-BR')}
                      <span className="text-xs text-muted-foreground">
                        ({formatDistanceToNow(new Date(checkIn.date), { addSuffix: true, locale: ptBR })})
                      </span>
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <MoodBadge value={checkIn.moodScore} />
                    <Badge variant="outline">PDI: {checkIn.pdiProgress}/5</Badge>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                <div className="space-y-3">
                  {/* Destaques */}
                  <div>
                    <h4 className="text-sm font-semibold text-green-700 dark:text-green-400 mb-1">✨ Destaques Positivos</h4>
                    <p className="text-sm text-foreground">{checkIn.highlights}</p>
                  </div>

                  {/* Pontos de Atenção */}
                  {checkIn.concerns && (
                    <div>
                      <h4 className="text-sm font-semibold text-orange-700 dark:text-orange-400 mb-1">⚠️ Pontos de Atenção</h4>
                      <p className="text-sm text-foreground">{checkIn.concerns}</p>
                    </div>
                  )}

                  {/* Pedidos de Apoio */}
                  {checkIn.supportNeeds && (
                    <div>
                      <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-400 mb-1">🤝 Pedidos de Apoio</h4>
                      <p className="text-sm text-foreground">{checkIn.supportNeeds}</p>
                    </div>
                  )}

                  {/* Acordos */}
                  {checkIn.agreements && checkIn.agreements.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-purple-700 dark:text-purple-400 mb-2">📋 Acordos/Próximos Passos</h4>
                      <ul className="space-y-1">
                        {checkIn.agreements.map((agreement) => (
                          <li key={agreement.id} className="flex items-start gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={agreement.isCompleted}
                              readOnly
                              className="mt-1"
                            />
                            <span className={agreement.isCompleted ? 'line-through text-muted-foreground' : 'text-foreground'}>
                              {agreement.description}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Observações */}
                  {checkIn.observations && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-1">💭 Observações</h4>
                      <p className="text-sm text-muted-foreground italic">{checkIn.observations}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="pt-6 text-center text-muted-foreground">
              Nenhum check-in encontrado para este período.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
